import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface UsageRecord {
  id: string;
  couponCode: string;
  customer: string;
  orderNumber: string;
  channel: "pos" | "online";
  discount: number;
  orderTotal: number;
  date: string;
  time: string;
  location?: string;
}

const MOCK_USAGE: UsageRecord[] = [
  { id: "1", couponCode: "WELCOME20", customer: "Juan Pérez", orderNumber: "ORD-2024-001", channel: "pos", discount: 45.00, orderTotal: 225.00, date: "2024-02-15", time: "14:32", location: "Sucursal Centro" },
  { id: "2", couponCode: "SAVE10", customer: "María García", orderNumber: "ORD-2024-002", channel: "online", discount: 10.00, orderTotal: 89.99, date: "2024-02-15", time: "13:15" },
  { id: "3", couponCode: "VIP15", customer: "Carlos López", orderNumber: "ORD-2024-003", channel: "pos", discount: 22.50, orderTotal: 150.00, date: "2024-02-15", time: "11:45", location: "Sucursal Norte" },
  { id: "4", couponCode: "WELCOME20", customer: "Ana Martínez", orderNumber: "ORD-2024-004", channel: "online", discount: 30.00, orderTotal: 150.00, date: "2024-02-14", time: "20:10" },
  { id: "5", couponCode: "FLASH50", customer: "Roberto Díaz", orderNumber: "ORD-2024-005", channel: "pos", discount: 75.00, orderTotal: 150.00, date: "2024-02-14", time: "15:22", location: "Sucursal Sur" },
  { id: "6", couponCode: "SUMMER25", customer: "Laura Sánchez", orderNumber: "ORD-2024-006", channel: "online", discount: 50.00, orderTotal: 200.00, date: "2024-02-14", time: "10:05" },
  { id: "7", couponCode: "VIP15", customer: "Miguel Torres", orderNumber: "ORD-2024-007", channel: "pos", discount: 18.00, orderTotal: 120.00, date: "2024-02-13", time: "16:48", location: "Sucursal Centro" },
  { id: "8", couponCode: "SAVE10", customer: "Patricia Ruiz", orderNumber: "ORD-2024-008", channel: "online", discount: 10.00, orderTotal: 75.50, date: "2024-02-13", time: "09:30" },
];

export default function UsageHistorySection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [channelFilter, setChannelFilter] = useState<string>("all");
  const [dateFilter, setDateFilter] = useState<string>("all");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const filteredUsage = MOCK_USAGE.filter((record) => {
    const matchesSearch =
      record.couponCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.orderNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesChannel = channelFilter === "all" || record.channel === channelFilter;
    return matchesSearch && matchesChannel;
  });

  const totalDiscount = MOCK_USAGE.reduce((acc, r) => acc + r.discount, 0);
  const totalOrders = MOCK_USAGE.length;
  const posUsage = MOCK_USAGE.filter((r) => r.channel === "pos").length;
  const onlineUsage = MOCK_USAGE.filter((r) => r.channel === "online").length;

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>
            {t("posUsageHistory.title")}
          </h1>
          <p className={themeClasses.textSecondary}>
            {t("posUsageHistory.subtitle")}
          </p>
        </div>
        <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} flex items-center gap-2`}>
          <i className="ri-download-line"></i>
          {t("posUsageHistory.exportHistory")}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posUsageHistory.totalRedemptions")}</span>
            <i className="ri-ticket-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{totalOrders}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posUsageHistory.totalDiscounts")}</span>
            <i className="ri-money-dollar-circle-line text-pink-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>${totalDiscount.toFixed(2)}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posUsageHistory.posRedemptions")}</span>
            <i className="ri-store-line text-blue-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{posUsage}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("posUsageHistory.onlineRedemptions")}</span>
            <i className="ri-global-line text-violet-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{onlineUsage}</p>
        </div>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`p-4 border-b ${themeClasses.border} flex flex-col sm:flex-row gap-4`}>
          <div className="flex-1 relative">
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
            <input
              type="text"
              placeholder={t("posUsageHistory.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>

          <select
            value={channelFilter}
            onChange={(e) => setChannelFilter(e.target.value)}
            className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text}`}
          >
            <option value="all">{t("posUsageHistory.allChannels")}</option>
            <option value="pos">POS</option>
            <option value="online">Online</option>
          </select>

          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text}`}
          >
            <option value="all">{t("posUsageHistory.allDates")}</option>
            <option value="today">{t("posUsageHistory.today")}</option>
            <option value="week">{t("posUsageHistory.thisWeek")}</option>
            <option value="month">{t("posUsageHistory.thisMonth")}</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${themeClasses.cardBg} border-b ${themeClasses.border}`}>
              <tr>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>
                  {t("posUsageHistory.dateTime")}
                </th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>
                  {t("posUsageHistory.coupon")}
                </th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>
                  {t("posUsageHistory.customer")}
                </th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>
                  {t("posUsageHistory.order")}
                </th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>
                  {t("posUsageHistory.channel")}
                </th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>
                  {t("posUsageHistory.discount")}
                </th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>
                  {t("posUsageHistory.orderTotal")}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredUsage.map((record) => (
                <tr key={record.id} className={`border-b ${themeClasses.border} ${themeClasses.tableRowHover}`}>
                  <td className="px-4 py-3">
                    <div>
                      <p className={`font-medium ${themeClasses.text}`}>{record.date}</p>
                      <p className={`text-sm ${themeClasses.textSecondary}`}>{record.time}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-mono font-bold ${themeClasses.text}`}>{record.couponCode}</span>
                  </td>
                  <td className={`px-4 py-3 ${themeClasses.text}`}>{record.customer}</td>
                  <td className="px-4 py-3">
                    <span className={`text-sm ${themeClasses.textSecondary}`}>{record.orderNumber}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs ${
                      record.channel === "pos" 
                        ? "bg-blue-500/20 text-blue-500" 
                        : "bg-violet-500/20 text-violet-500"
                    }`}>
                      {record.channel === "pos" ? "POS" : "WEB"}
                    </span>
                    {record.location && (
                      <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>{record.location}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-primary-500 font-bold">-${record.discount.toFixed(2)}</span>
                  </td>
                  <td className={`px-4 py-3 text-right font-medium ${themeClasses.text}`}>
                    ${record.orderTotal.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={`p-4 border-t ${themeClasses.border} flex justify-between items-center`}>
          <p className={`text-sm ${themeClasses.textSecondary}`}>
            {t("posUsageHistory.showingResults", { count: filteredUsage.length, total: MOCK_USAGE.length })}
          </p>
          <div className="flex items-center gap-2">
            <button aria-label={t("general.previous", { ns: "common" })} className={`px-3 py-1 rounded-lg ${themeClasses.cardBg} ${themeClasses.text} text-sm`}>
              <i className="ri-arrow-left-s-line"></i>
            </button>
            <span className={`px-3 py-1 rounded-lg bg-primary-500 text-gray-900 text-sm font-medium`}>1</span>
            <button aria-label={t("general.next", { ns: "common" })} className={`px-3 py-1 rounded-lg ${themeClasses.cardBg} ${themeClasses.text} text-sm`}>
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
